import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, AlertCircle, Check, Pencil, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Returns the last Monday at 00:00 IST as a Date.
const lastMondayMidnight = () => {
  const now = new Date();
  const day = now.getDay();
  const daysSinceMon = (day + 6) % 7; // Mon=0 .. Sun=6
  const d = new Date(now);
  d.setDate(now.getDate() - daysSinceMon);
  d.setHours(0, 0, 0, 0);
  return d;
};

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const compactNum = (n) => {
  const v = Number(n || 0);
  if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
  return v.toLocaleString('en-IN');
};

// Tiny inline SVG sparkline (no library) — used inside the expanded pill.
const Sparkline = ({ data = [], width = 56, height = 18, className = '' }) => {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const stepX = width / (data.length - 1);
  const points = data
    .map((v, i) => `${i * stepX},${height - ((v - min) / span) * (height - 2) - 1}`)
    .join(' ');
  const last = data[data.length - 1];
  const lastX = (data.length - 1) * stepX;
  const lastY = height - ((last - min) / span) * (height - 2) - 1;
  return (
    <svg width={width} height={height} className={className} aria-hidden="true">
      <polyline fill="none" stroke="#2EB4AC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" points={points} />
      <circle cx={lastX} cy={lastY} r={1.8} fill="#2EB4AC" />
    </svg>
  );
};

export default function CostTrackerWidget() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [history, setHistory] = useState([]);

  // form state
  const [credits, setCredits] = useState('');
  const [rate, setRate] = useState('');
  const [saving, setSaving] = useState(false);

  const wrapRef = useRef(null);

  const fetchAll = async () => {
    try {
      const [s, h] = await Promise.all([
        fetch(`${API_URL}/api/cost-tracker`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
        fetch(`${API_URL}/api/cost-tracker/history?weeks=12`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
      ]);
      if (s) setState(s);
      if (h) setHistory(h.history || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Click-outside to collapse
  useEffect(() => {
    if (!expanded) return undefined;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setExpanded(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [expanded]);

  // Reset form when dialog opens
  useEffect(() => {
    if (editOpen && state) {
      setCredits(state.total_credits ?? '');
      setRate(state.cost_per_credit_inr ?? 13.7);
    }
  }, [editOpen, state]);

  const stale = useMemo(() => {
    if (!state?.updated_at) return true;
    return new Date(state.updated_at) < lastMondayMidnight();
  }, [state]);

  const totalCost = useMemo(() => {
    if (!state) return 0;
    return Number(state.total_credits || 0) * Number(state.cost_per_credit_inr || 0);
  }, [state]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/cost-tracker`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          total_credits: credits === '' ? 0 : Number(credits),
          cost_per_credit_inr: rate === '' ? 13.7 : Number(rate),
        }),
      });
      if (!r.ok) throw new Error();
      const j = await r.json();
      setState(j);
      // Refresh history so the chart includes the new snapshot
      try {
        const hr = await fetch(`${API_URL}/api/cost-tracker/history?weeks=12`, { credentials: 'include' });
        if (hr.ok) {
          const hj = await hr.json();
          setHistory(hj.history || []);
        }
      } catch { /* silent */ }
      toast.success('Cost tracker updated');
      setEditOpen(false);
      setExpanded(false);
    } catch {
      toast.error('Could not save');
    } finally {
      setSaving(false);
    }
  };

  // Compute week-on-week delta for the trend chip
  const trend = useMemo(() => {
    if (history.length < 2) return null;
    const last = history[history.length - 1].credits;
    const prev = history[history.length - 2].credits;
    if (!prev) return null;
    const delta = last - prev;
    const pct = (delta / prev) * 100;
    return { delta, pct };
  }, [history]);

  if (loading || !state) return null;

  return (
    <>
      <div ref={wrapRef} className="relative">
        {!expanded ? (
          // === COLLAPSED — discreet icon button (blends with header) ===
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="relative h-10 w-10 rounded-full hover:bg-slate-100 transition-colors flex items-center justify-center"
            data-testid="cost-tracker-widget"
            title="Cost tracker"
          >
            <img src="/icons/cost-rupee.png" alt="" className="h-7 w-7 hover:scale-110 transition-transform" />
            {stale && (
              <span className="absolute top-1 right-1 flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500 border-2 border-white" />
              </span>
            )}
          </button>
        ) : (
          // === EXPANDED — Variant C stat-card + mini sparkline ===
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="group relative inline-flex items-stretch gap-0 h-11 rounded-xl border border-slate-200 bg-white hover:border-[#2EB4AC]/40 hover:shadow-sm transition-all overflow-hidden animate-in fade-in slide-in-from-right-2 duration-200"
            data-testid="cost-tracker-widget-expanded"
          >
            <div className="flex items-center gap-2 px-3 border-r border-slate-200 bg-slate-50/50">
              <img src="/icons/cost-rupee.png" alt="" className="h-5 w-5" />
              <div className="flex flex-col items-start leading-none">
                <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider">Credits</span>
                <span className="text-sm font-bold text-slate-900 mt-0.5 tabular-nums">{compactNum(state.total_credits)}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3">
              <div className="flex flex-col items-start leading-none">
                <span className="text-[9px] font-semibold text-[#2EB4AC] uppercase tracking-wider">Spent</span>
                <span className="text-sm font-bold text-slate-900 mt-0.5 tabular-nums">{inr(totalCost)}</span>
              </div>
              {/* Mini sparkline (only when ≥2 weekly points) */}
              {history.length >= 2 && (
                <Sparkline data={history.map(h => h.credits)} className="ml-1" />
              )}
              {stale ? (
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-rose-500" />
                </span>
              ) : (
                <Pencil className="h-3 w-3 text-slate-300 group-hover:text-[#2EB4AC] transition-colors" />
              )}
            </div>
          </button>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg" data-testid="cost-tracker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <img src="/icons/cost-rupee.png" alt="" className="h-6 w-6" />
              App Development Cost Tracker
            </DialogTitle>
          </DialogHeader>

          {stale && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>It's a new week — please update the credits consumed since last Monday.</span>
            </div>
          )}

          {/* Week-on-week trendline */}
          {history.length >= 2 ? (
            <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50/40 p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em]">Week-on-Week Credits</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">Last {history.length} weeks · Mondays</p>
                </div>
                {trend && (
                  <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold ${
                    trend.delta > 0 ? 'bg-rose-50 text-rose-600 border border-rose-200'
                    : trend.delta < 0 ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                    : 'bg-slate-50 text-slate-500 border border-slate-200'
                  }`}>
                    {trend.delta > 0 ? <TrendingUp className="h-3 w-3" /> : trend.delta < 0 ? <TrendingDown className="h-3 w-3" /> : null}
                    {trend.delta > 0 ? '+' : ''}{compactNum(Math.abs(trend.delta))} cr ({trend.pct >= 0 ? '+' : ''}{trend.pct.toFixed(0)}%)
                  </div>
                )}
              </div>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={history} margin={{ top: 8, right: 6, bottom: 0, left: -12 }}>
                    <defs>
                      <linearGradient id="costFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2EB4AC" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#2EB4AC" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis
                      dataKey="week_start"
                      tickFormatter={(v) => format(new Date(v), 'd MMM')}
                      stroke="#94a3b8"
                      fontSize={10}
                      tickLine={false}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      fontSize={10}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(v) => compactNum(v)}
                      width={40}
                    />
                    <Tooltip
                      contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12, padding: '6px 10px' }}
                      labelFormatter={(v) => `Week of ${format(new Date(v), 'd MMM')}`}
                      formatter={(value, name) => name === 'credits'
                        ? [compactNum(value) + ' credits', 'Cumulative']
                        : ['₹' + Number(value).toLocaleString('en-IN'), 'Cost']}
                    />
                    <Area
                      type="monotone"
                      dataKey="credits"
                      stroke="#2EB4AC"
                      strokeWidth={2}
                      fill="url(#costFill)"
                      dot={{ r: 3, stroke: '#2EB4AC', strokeWidth: 2, fill: '#fff' }}
                      activeDot={{ r: 4 }}
                    />
                    <ReferenceLine y={0} stroke="#cbd5e1" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 px-4 py-3 text-[11px] text-slate-500 text-center">
              Save updates each week to build the trendline.
            </div>
          )}

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-medium text-slate-600">Total credits consumed</Label>
              <Input
                type="number"
                value={credits}
                onChange={(e) => setCredits(e.target.value)}
                placeholder="e.g. 1250"
                className="mt-1"
                min="0"
                step="any"
                data-testid="cost-tracker-credits-input"
              />
            </div>

            <div>
              <Label className="text-xs font-medium text-slate-600">Estimated cost per credit (₹)</Label>
              <Input
                type="number"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="13.70"
                className="mt-1"
                min="0"
                step="0.01"
                data-testid="cost-tracker-rate-input"
              />
              <p className="text-[11px] text-slate-400 mt-1">Default ₹13.70 — edit anytime as billing changes.</p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Estimated total</p>
                <p className="text-2xl font-bold text-slate-900 mt-0.5 tracking-tight">
                  {inr(Number(credits || 0) * Number(rate || 0))}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-slate-400">{Number(credits || 0).toLocaleString('en-IN')} credits</p>
                <p className="text-[10px] text-slate-400">× ₹{Number(rate || 0)}</p>
              </div>
            </div>

            {state.updated_at && (
              <p className="text-[11px] text-slate-500">
                Last updated {format(new Date(state.updated_at), 'PPp')} {state.updated_by ? `by ${state.updated_by.split('@')[0]}` : ''}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#2EB4AC] hover:bg-[#259993] text-white"
              data-testid="cost-tracker-save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
